import { PayloadAction } from '@reduxjs/toolkit';
import { createAppSlice } from '../../app/createAppSlice';
import { Device } from './interfaces/api';
import { CustomerConfig } from '../../interfaces/state';

/**
 * Data for initial state
 */
export interface TradeInState {
    /**
     * This property represents the current state
     * of the tradeIn-option-picker.
     * `true` if 'yes', `false` if 'no'
     */
    isTradeInSelected: boolean;
    /**
     * Current FormSuggestInput value
     */
    suggestInputValue: string;
    /**
     * Selected device ID
     */
    selectedTradeInDeviceId: string | null;
    /**
     * Selected device object from session storage.
     * Is only filled once within setInitialState reducer.
     * Will be nulled as soon as the selected device
     * is changed or removed.
     */
    selectedTradeInDeviceFromStorage: Device | null;
}

/**
 * Initial state
 */
const initialState: TradeInState = {
    isTradeInSelected: false,
    suggestInputValue: '',
    selectedTradeInDeviceId: null,
    selectedTradeInDeviceFromStorage: null,
};

/**
 * SetInitialStateArgs
 */
export interface SetInitialStateArgs {
    /* eslint-disable jsdoc/require-jsdoc */
    customerConfig: CustomerConfig;
    selectedTradeInDevice: Device | null;
    /* eslint-enable jsdoc/require-jsdoc */
}

const tradeInSlice = createAppSlice({
    name: 'TradeIn',
    initialState,
    // eslint-disable-next-line arrow-body-style
    reducers: create => {
        return {
            setInitialState: create.reducer((state, action: PayloadAction<SetInitialStateArgs>) => {
                if (action.payload.selectedTradeInDevice) {
                    state.isTradeInSelected = action.payload.customerConfig.isTradeIn;
                    state.selectedTradeInDeviceId = action.payload.selectedTradeInDevice.id;
                    state.selectedTradeInDeviceFromStorage = action.payload.selectedTradeInDevice;
                }
                else {
                    state.isTradeInSelected = false;
                }
            }),
            setIsTradeInSelected: create.reducer((state, action: PayloadAction<boolean>) => {
                state.isTradeInSelected = action.payload;
            }),
            setSuggestInputValue: create.reducer((state, action: PayloadAction<string>) => {
                state.suggestInputValue = action.payload;
            }),
            setSelectedTradeInDeviceId: create.reducer((state, action: PayloadAction<string>) => {
                state.selectedTradeInDeviceId = action.payload;
                state.selectedTradeInDeviceFromStorage = null;
            }),
            deleteSelectedTradeInDevice: create.reducer(state => {
                state.selectedTradeInDeviceId = null;
                state.selectedTradeInDeviceFromStorage = null;
                state.suggestInputValue = '';
            }),
        };
    },
    selectors: {
        selectIsTradeInSelected: sliceState => sliceState.isTradeInSelected,
        selectSuggestInputValue: sliceState => sliceState.suggestInputValue,
        selectSelectedTradeInDeviceId: sliceState => sliceState.selectedTradeInDeviceId,
        selectSelectedTradeInDeviceFromStorage: sliceState => sliceState.selectedTradeInDeviceFromStorage,
    },
});

export default tradeInSlice;

export const {
    setInitialState,
    setIsTradeInSelected,
    setSuggestInputValue,
    setSelectedTradeInDeviceId,
    deleteSelectedTradeInDevice,
} = tradeInSlice.actions;

export const {
    selectIsTradeInSelected,
    selectSuggestInputValue,
    selectSelectedTradeInDeviceId,
    selectSelectedTradeInDeviceFromStorage,
} = tradeInSlice.selectors;
